// SEQ-180 E14: sticky filter bar for the P&L analytics page. Reuses the
// app's .form-input / .btn / .card classes (src/index.css) rather than
// introducing new controls. Project and status pickers are small, self
// contained multi-select popovers (the app's own DashboardFilterBar is a
// full-screen modal built for a different page; a page this data-dense
// needs its controls visible, not hidden behind a modal trigger).
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Bookmark, ChevronDown, Trash2 } from "lucide-react";
import type { DatePresetName } from "../../lib/pnl/fiscalYear";
import {
  DEFAULT_PNL_FILTER_STATE,
  type PnlAnalyticsFilterState,
  type PnlProjectOption,
  type PnlSavedDatePreset,
} from "../../features/pnlAnalytics/filterState";

const DATE_PRESET_OPTIONS: { value: DatePresetName; label: string }[] = [
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "quarter_to_date", label: "Quarter to date" },
  { value: "year_to_date", label: "Year to date" },
  { value: "financial_year_to_date", label: "Financial year to date" },
  { value: "last_financial_year", label: "Last financial year" },
  { value: "project_lifetime", label: "Project lifetime" },
  { value: "custom", label: "Custom range" },
];

// Plan decision D8: the production status vocabulary, not the ticket's.
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "upcoming", label: "Upcoming" },
  { value: "completed", label: "Completed" },
];

interface MultiSelectProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}

function MultiSelect({ label, options, selected, onChange, placeholder }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const summary = selected.length === 0 ? placeholder : `${selected.length} selected`;

  return (
    <div ref={rootRef} style={{ position: "relative", flex: "1 1 180px", minWidth: 0 }}>
      <label id={labelId} style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
        {label}
      </label>
      <button
        type="button"
        className="form-input"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%", cursor: "pointer" }}
        aria-haspopup="true"
        aria-expanded={open}
        aria-labelledby={labelId}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{ color: selected.length ? "var(--text-primary)" : "var(--text-muted)" }}>{summary}</span>
        <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : undefined, transition: "transform 0.15s" }} />
      </button>
      {open && (
        <div
          role="group"
          aria-labelledby={labelId}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 30,
            width: "max-content",
            minWidth: "100%",
            maxWidth: "min(320px, 85vw)",
            maxHeight: 260,
            overflowY: "auto",
            background: "var(--bg-secondary)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--border-radius-md)",
            boxShadow: "var(--shadow-lg)",
            padding: 6,
          }}
        >
          {options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-hover)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(opt.value)}
                  style={{ margin: 0, accentColor: "var(--seq-blue)" }}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
          {options.length === 0 && (
            <div style={{ padding: 8, fontSize: 12, color: "var(--text-muted)" }}>No options</div>
          )}
        </div>
      )}
    </div>
  );
}

interface PnlAnalyticsFilterBarProps {
  projects: PnlProjectOption[];
  value: PnlAnalyticsFilterState;
  onChange: (next: PnlAnalyticsFilterState) => void;
  /**
   * SEQ-180 phase 2 (plan decision D19): the OPEN dashboard's own saved
   * ranges, listed in this dropdown alongside the eight built-in presets and
   * gone again when a different dashboard is opened. They are stored on the
   * dashboard row, so a shared dashboard carries its presets to everyone who
   * opens it.
   */
  savedPresets?: PnlSavedDatePreset[];
  /** False on the read-only default dashboard, and for a non-owner of a shared one. */
  canManagePresets?: boolean;
  onSavePreset?: (name: string, from: string, to: string) => void;
  onDeletePreset?: (id: string) => void;
}

const SAVED_PRESET_PREFIX = "preset:";

export function PnlAnalyticsFilterBar({
  projects,
  value,
  onChange,
  savedPresets = [],
  canManagePresets = false,
  onSavePreset,
  onDeletePreset,
}: PnlAnalyticsFilterBarProps) {
  const [presetName, setPresetName] = useState("");
  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name })),
    [projects],
  );

  return (
    <div
      className="card"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        gap: 16,
        padding: "14px 20px",
        marginBottom: 20,
      }}
    >
      <div style={{ flex: "1 1 190px", minWidth: 0 }}>
        <label htmlFor="pnl-date-preset" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
          Date range
        </label>
        <select
          id="pnl-date-preset"
          className="form-input"
          value={value.customPresetId ? `${SAVED_PRESET_PREFIX}${value.customPresetId}` : value.preset}
          onChange={(e) => {
            const selection = e.target.value;
            if (selection.startsWith(SAVED_PRESET_PREFIX)) {
              onChange({ ...value, customPresetId: selection.slice(SAVED_PRESET_PREFIX.length) });
              return;
            }
            onChange({ ...value, preset: selection as DatePresetName, customPresetId: null });
          }}
          style={{ width: "100%", minWidth: 0 }}
        >
          {DATE_PRESET_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
          {savedPresets.length > 0 && (
            <optgroup label="Saved on this dashboard">
              {savedPresets.map((preset) => (
                <option key={preset.id} value={`${SAVED_PRESET_PREFIX}${preset.id}`}>{preset.name}</option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      {value.customPresetId && canManagePresets && onDeletePreset && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            onDeletePreset(value.customPresetId as string);
            onChange({ ...value, customPresetId: null });
          }}
        >
          <Trash2 size={13} aria-hidden="true" /> Delete this preset
        </button>
      )}

      {value.preset === "custom" && !value.customPresetId && (
        <>
          <div style={{ flex: "1 1 150px", minWidth: 0 }}>
            <label htmlFor="pnl-custom-from" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
              From month
            </label>
            <input
              id="pnl-custom-from"
              type="month"
              className="form-input"
              style={{ width: "100%" }}
              value={value.customFrom ?? ""}
              onChange={(e) => onChange({ ...value, customFrom: e.target.value || null })}
            />
          </div>
          <div style={{ flex: "1 1 150px", minWidth: 0 }}>
            <label htmlFor="pnl-custom-to" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
              To month
            </label>
            <input
              id="pnl-custom-to"
              type="month"
              className="form-input"
              style={{ width: "100%" }}
              value={value.customTo ?? ""}
              onChange={(e) => onChange({ ...value, customTo: e.target.value || null })}
            />
          </div>
        </>
      )}

      {value.preset === "custom" &&
        !value.customPresetId &&
        canManagePresets &&
        onSavePreset &&
        value.customFrom &&
        value.customTo && (
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <label htmlFor="pnl-preset-name" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
              Save this range
            </label>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                id="pnl-preset-name"
                type="text"
                className="form-input"
                style={{ flex: "1 1 auto", minWidth: 0 }}
                maxLength={60}
                placeholder="Name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={presetName.trim().length === 0}
                onClick={() => {
                  const [toYear, toMonth] = (value.customTo as string).split("-").map(Number);
                  const lastDay = new Date(toYear, toMonth, 0).getDate();
                  onSavePreset(
                    presetName.trim(),
                    `${value.customFrom}-01`,
                    `${value.customTo}-${String(lastDay).padStart(2, "0")}`,
                  );
                  setPresetName("");
                }}
              >
                <Bookmark size={13} aria-hidden="true" /> Save
              </button>
            </div>
          </div>
        )}

      <MultiSelect
        label="Projects"
        options={projectOptions}
        selected={value.projectIds}
        onChange={(projectIds) => onChange({ ...value, projectIds })}
        placeholder="All projects"
      />

      <MultiSelect
        label="Status"
        options={STATUS_OPTIONS}
        selected={value.statuses}
        onChange={(statuses) => onChange({ ...value, statuses })}
        placeholder="All statuses"
      />

      {(value.projectIds.length > 0 ||
        value.statuses.length > 0 ||
        Boolean(value.customPresetId) ||
        value.preset !== DEFAULT_PNL_FILTER_STATE.preset) && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => onChange(DEFAULT_PNL_FILTER_STATE)}
        >
          Reset filters
        </button>
      )}
    </div>
  );
}
